import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import { useParams } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import { PlusIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid'
import _ from 'lodash'
import dayjs from 'dayjs'

// Components
import { Button } from '../../components/Button'
import { CalendarInput } from '../../components/CalendarInput'
import {
  DateEditor,
  TextAreaEditor,
  TextEditor,
  renderSelectEditor,
  renderMultiSelectFilter,
  renderStatusTag,
} from '../../components/CustomEditor'
import { DataTable, CUSTOM_FILTER_OPTIONS } from '../../components/DataTable'
import { Modal } from '../../components/Modal'
import { PageContainer } from '../../components/PageContainer'
import { Select } from '../../components/Select'
import { StateContainer } from '../../components/StateContainer'
import { TextArea } from '../../components/TextArea'
import { TextInput } from '../../components/TextInput'

// Services
import { getClient } from '../../services/clients.service'
import {
  createClientDisclosure,
  deleteClientDisclosure,
  getClientDisclosures,
  updateClientDisclosure,
} from '../../services/disclosures.service'

// Stores
import { ClientDashboardStoreContext } from '../../stores/ClientDashboardStore'

// Utils & Hooks
import { configureFilterQuery, toast } from '../../utils/helpers'
import { usePagination, useSorting } from '../../hooks/DataTableManagement'
import {
  DISCLOSURE_DESCRIPTION_OPTIONS,
  ORIENTATION,
  REPORT_OPTIONS,
  YES_OR_NO,
} from '../../utils/constants'

const DEFAULT_FILTERS = {
  listOfDescriptions: { value: [] },
  useHouseholdTool: { value: [] },
  reportFormat: { value: [] },
  orientation: { value: [] },
}

/**
 *
 * Disclosures
 *
 */
const Disclosures = observer(() => {
  // Context
  const { clientId } = useParams()
  const { client, setClient } = useContext(ClientDashboardStoreContext)

  // Pagination
  const { pagination, setTotalRecords } = usePagination(10)
  const { perPage, currentPage } = pagination

  // Sorting
  const { sorting } = useSorting('firm_name')
  const { sortedColumn } = sorting

  // State
  const [loadingClient, setLoadingClient] = useState(true)
  const [loadingData, setLoadingData] = useState(true)
  const [loadingDisclosures, setLoadingDisclosures] = useState(false)
  const [columns, setColumns] = useState([])
  const [loadingDelete, setLoadingDelete] = useState(false)
  const [error, setError] = useState(null)
  const [disclosures, setDisclosures] = useState([])
  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [showAddDisclosureModal, setShowAddDisclosureModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [search, setSearch] = useState('')
  const [refreshCounter, setRefreshCounter] = useState(0)

  const refreshDisclosures = () => setRefreshCounter((c) => c + 1)

  const BASE_URL = `/clients/${clientId}/disclosures/`
  const searchInputRef = useRef()

  const handleSuccess = (m) => toast(m, 'success')
  const handleErrors = (m) => toast(m, 'error')

  /**
   * Gets the updated list of disclosures; updates pagination.
   * @param {string} url
   */
  const getUpdatedDisclosures = async (url) => {
    const response = await getClientDisclosures(url, setError, setLoadingDisclosures)

    if (response) {
      setTotalRecords(response.count)
      setDisclosures(response.results)
    }
  }

  useEffect(() => {
    const getClientData = async () => {
      const updatedClient = await getClient(clientId, setError, setLoadingClient)
      if (updatedClient) {
        setClient(updatedClient)
      }

      // Update editors and filters to include options
      const updatedColumns = [
        {
          field: 'firmName',
          header: 'Firm Name',
          editor: TextEditor,
          sortable: true,
          style: { minWidth: '250px' },
        },
        {
          field: 'firmDefinition',
          header: 'Firm Definition',
          editor: TextAreaEditor,
          sortable: true,
          style: { minWidth: '400px' },
        },
        {
          field: 'listOfDescriptions',
          header: 'List of Descriptions',
          sortable: true,
          body: (rowData) => rowData.listOfDescriptions,
          style: { minWidth: '250px' },
          editor: (options) =>
            renderSelectEditor(
              DISCLOSURE_DESCRIPTION_OPTIONS,
              options.rowData.listOfDescriptions,
              'Select an Option',
              options,
            ),
          filterField: 'listOfDescriptions',
          ...CUSTOM_FILTER_OPTIONS,
          filterElement: (options) =>
            renderMultiSelectFilter(
              DISCLOSURE_DESCRIPTION_OPTIONS,
              (selected) => {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  listOfDescriptions: {
                    value: selected,
                  },
                }))
              },
              'Filter by Descriptions',
              options,
            ),
        },
        {
          field: 'verifiedFrom',
          header: 'Verified From',
          body: (row) => row?.verifiedFrom && dayjs(row.verifiedFrom).format('MM/DD/YYYY'),
          editor: DateEditor,
          sortable: true,
          style: { minWidth: '150px' },
        },
        {
          field: 'verifiedTo',
          header: 'Verified To',
          body: (row) => row.verifiedTo && dayjs(row.verifiedTo).format('MM/DD/YYYY'),
          editor: DateEditor,
          sortable: true,
          style: { minWidth: '150px' },
        },
        {
          field: 'gipsReportThroughDate',
          header: 'GIPS Report Through Date',
          body: (row) =>
            row.gipsReportThroughDate && dayjs(row.gipsReportThroughDate).format('MM/DD/YYYY'),
          editor: DateEditor,
          sortable: true,
          style: { minWidth: '150px' },
        },
        {
          field: 'useHouseholdTool',
          header: 'Use Household Tool',
          sortable: true,
          body: (rowData) => renderStatusTag(rowData.useHouseholdTool === 'Yes'),
          style: { minWidth: '190px' },
          editor: (options) =>
            renderSelectEditor(
              YES_OR_NO,
              options.rowData.useHouseholdTool,
              'Select an Option',
              options,
            ),
          filterField: 'useHouseholdTool',
          ...CUSTOM_FILTER_OPTIONS,
          filterElement: (options) =>
            renderMultiSelectFilter(
              YES_OR_NO,
              (selected) => {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  useHouseholdTool: {
                    value: selected,
                  },
                }))
              },
              'Filter by Uses Household Tool',
              options,
            ),
        },
        {
          field: 'reportFormat',
          header: 'Report Format',
          sortable: true,
          body: (rowData) => rowData.reportFormat,
          style: { minWidth: '170px' },
          editor: (options) =>
            renderSelectEditor(
              REPORT_OPTIONS,
              options.rowData.reportFormat,
              'Select an Option',
              options,
            ),
          filterField: 'reportFormat',
          ...CUSTOM_FILTER_OPTIONS,
          filterElement: (options) =>
            renderMultiSelectFilter(
              REPORT_OPTIONS,
              (selected) => {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  reportFormat: {
                    value: selected,
                  },
                }))
              },
              'Filter by Report Format',
              options,
            ),
        },
        {
          field: 'color',
          header: 'Color',
          editor: TextEditor,
          sortable: true,
          style: { minWidth: '150px' },
        },
        {
          field: 'logo',
          header: 'Logo',
          editor: TextEditor,
          sortable: true,
          style: { minWidth: '250px' },
        },
        {
          field: 'orientation',
          header: 'Orientation',
          sortable: true,
          body: (rowData) => rowData.orientation,
          style: { minWidth: '170px' },
          editor: (options) =>
            renderSelectEditor(
              ORIENTATION,
              options.rowData.orientation,
              'Select an Option',
              options,
            ),
          filterField: 'orientation',
          ...CUSTOM_FILTER_OPTIONS,
          filterElement: (options) =>
            renderMultiSelectFilter(
              ORIENTATION,
              (selected) => {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  orientation: {
                    value: selected,
                  },
                }))
              },
              'Filter by Orientation',
              options,
            ),
        },
        {
          field: 'footnote',
          header: 'Footnote',
          editor: TextAreaEditor,
          sortable: true,
          style: { minWidth: '300px' },
        },
      ]

      setColumns(updatedColumns)
      setLoadingData(false)
    }

    getClientData()
  }, [clientId])

  /**
   * When the filter, sort, current page, or row count changes, get the updated list of disclosures.
   */
  useEffect(() => {
    if (clientId) {
      const queryFilters = configureFilterQuery({
        list_of_descriptions: filters.listOfDescriptions || DEFAULT_FILTERS.listOfDescriptions,
        use_household_tool: filters.useHouseholdTool || DEFAULT_FILTERS.useHouseholdTool,
        report_format: filters.reportFormat || DEFAULT_FILTERS.reportFormat,
        orientation: filters.orientation || DEFAULT_FILTERS.orientation,
      })

      getUpdatedDisclosures(
        `${BASE_URL}?order_by=${sortedColumn}&limit=${perPage}&page=${currentPage}${
          search ? `&q=${search}` : ''
        }&${queryFilters}`,
      )
    }
  }, [perPage, clientId, sortedColumn, currentPage, filters, search, refreshCounter])

  const { control, clearErrors, handleSubmit, register, reset, setValue } = useForm()

  /**
   * Handles form submission by creating a new disclosure for the client.
   * @param {object} data
   */
  const onSubmit = async (data) => {
    const payload = { ...data }

    // Prevent saving a disclosure with no data
    const hasAnyValidField = _.some(payload, (v) => v !== null && v !== undefined && v !== '')
    if (!hasAnyValidField) {
      handleErrors('Please fill at least one field.')
      return
    }

    createClientDisclosure(
      client.id,
      {
        ...payload,
        verifiedFrom: payload.verifiedFrom
          ? dayjs(payload.verifiedFrom).format('YYYY-MM-DD')
          : null,
        verifiedTo: payload.verifiedTo ? dayjs(payload.verifiedTo).format('YYYY-MM-DD') : null,
        gipsReportThroughDate: payload.gipsReportThroughDate
          ? dayjs(payload.gipsReportThroughDate).format('YYYY-MM-DD')
          : null,
        listOfDescriptions: payload.listOfDescriptions?.id || null,
        orientation: payload.orientation?.id || null,
        reportFormat: payload.reportFormat?.id || null,
        useHouseholdTool: payload.useHouseholdTool?.id || null,
      },
      handleErrors,
      setLoadingData,
      (m) => {
        handleSuccess(m)
        reset()
        refreshDisclosures()
        setShowAddDisclosureModal(false)
      },
    )
  }

  /**
   * Debounces the search input.
   */
  const searchDisclosures = useCallback(_.debounce(setSearch, 500), [])

  /**
   * Handles row edit completion by updating the local `disclosures` with new data and
   * submitting it to the backend.
   * @param {object} event
   */
  const onRowEditComplete = async ({ newData, index }) => {
    const oldDisclosures = [...disclosures]
    const updatedData = [...disclosures]

    if (newData.verifiedFrom instanceof Date) {
      const [date] = newData.verifiedFrom.toISOString().split('T')
      // eslint-disable-next-line no-param-reassign
      newData.verifiedFrom = date
    }
    if (newData.verifiedTo instanceof Date) {
      const [date] = newData.verifiedTo.toISOString().split('T')
      // eslint-disable-next-line no-param-reassign
      newData.verifiedTo = date
    }
    if (newData.gipsReportThroughDate instanceof Date) {
      const [date] = newData.gipsReportThroughDate.toISOString().split('T')
      // eslint-disable-next-line no-param-reassign
      newData.gipsReportThroughDate = date
    }

    // Update the data
    updatedData[index] = newData

    // Optimistically update the data to prevent showing internal values
    setDisclosures(updatedData)

    await updateClientDisclosure(
      clientId,
      newData,
      (m) => {
        // Reset back to previous value
        setDisclosures(oldDisclosures)
        handleErrors(m)
      },
      () => {},
      (m) => handleSuccess(m),
    )
  }

  return (
    <PageContainer>
      <StateContainer error={error} loading={loadingClient}>
        <div className="flex size-full flex-col overflow-y-auto bg-background">
          <div className="space-y-6">
            <div className="flex flex-col gap-2">
              <div className="flex w-full flex-col justify-between sm:flex-row sm:items-center">
                <h2 className="text-xl font-semibold leading-6 text-gray-900">Disclosures</h2>

                <Button label="Add Disclosure" onClick={() => setShowAddDisclosureModal(true)} />
              </div>

              <TextInput
                className="w-full rounded-full py-2.5 pl-10 pr-4 placeholder:font-normal placeholder:text-gray-600 md:w-[450px]"
                icon={
                  <MagnifyingGlassIcon className="ml-2 h-5 text-gray-dark" aria-hidden="true" />
                }
                id="search"
                endIcon={
                  searchTerm ? (
                    <Button
                      type="button"
                      onClick={() => {
                        setSearchTerm('')
                        searchDisclosures(null)
                        searchInputRef.current.value = ''
                      }}
                      icon={<XMarkIcon className="mr-2 h-5 text-gray-dark" aria-hidden="true" />}
                      ariaLabel="Clear search"
                      iconOnly
                    />
                  ) : null
                }
                name="search"
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  searchDisclosures(e.target.value)
                }}
                placeholder="Looking for something?"
                ref={searchInputRef}
                value={searchTerm}
              />
            </div>

            <DataTable
              data={disclosures}
              columns={columns}
              loadingDelete={loadingDelete}
              hoverEffect={false}
              loading={loadingData || loadingDisclosures}
              pagination={pagination}
              sorting={sorting}
              onRowEditComplete={onRowEditComplete}
              onRowDeleteConfirm={(row) => {
                deleteClientDisclosure(clientId, row.id, handleErrors, setLoadingDelete, () => {
                  handleSuccess('Disclosure deleted.')
                  refreshDisclosures()
                })
              }}
              areRowsDeleteable
              areRowsEditable
              rowDeleteConfirm={(row) => ({
                title: `Delete Disclosure`,
                type: 'warning',
                message: (
                  <div className="flex flex-col">
                    <span className="">Are you sure you want to delete the disclosure:</span>
                    <span className="font-semibold">{row.firmName}</span>
                  </div>
                ),
              })}
              filters={filters}
              onFilter={(e) => setFilters(e.filters)}
            />
          </div>
        </div>
      </StateContainer>

      <Modal
        icon={<PlusIcon className="h-6 fill-white" />}
        open={showAddDisclosureModal}
        title="Add Disclosure"
        loading={loadingData}
        onClose={() => {
          setShowAddDisclosureModal(false)
          reset()
        }}
        content={
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="mt-8 flex size-full flex-col gap-4 pt-2"
          >
            <TextInput fullWidth label="Firm Name" name="firmName" {...register('firmName')} />

            <TextArea
              fullWidth
              label="Firm Definition"
              name="firmDefinition"
              {...register('firmDefinition')}
            />

            <Controller
              name="listOfDescriptions"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  value={value}
                  label="List of Descriptions"
                  style={{ width: '100%' }}
                  onChange={(option) => {
                    onChange(option)
                    clearErrors('listOfDescriptions')
                  }}
                  options={DISCLOSURE_DESCRIPTION_OPTIONS}
                />
              )}
            />

            <div className="flex flex-row gap-2">
              <Controller
                name="verifiedFrom"
                control={control}
                render={({ field: { value } }) => (
                  <CalendarInput
                    label="Verified From"
                    id="verifiedFrom"
                    fullWidth
                    value={value}
                    onChange={(d) => setValue('verifiedFrom', d)}
                  />
                )}
              />

              <Controller
                name="verifiedTo"
                control={control}
                render={({ field: { value } }) => (
                  <CalendarInput
                    label="Verified To"
                    id="verifiedTo"
                    fullWidth
                    value={value}
                    onChange={(d) => setValue('verifiedTo', d)}
                  />
                )}
              />
            </div>

            <Controller
              name="gipsReportThroughDate"
              control={control}
              render={({ field: { value } }) => (
                <CalendarInput
                  label="GIPS Report Through Date"
                  id="gipsReportThroughDate"
                  value={value}
                  onChange={(d) => setValue('gipsReportThroughDate', d)}
                />
              )}
            />

            <div className="flex flex-row gap-2">
              <Controller
                name="useHouseholdTool"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    value={value}
                    label="Use Household Tool"
                    style={{ width: '100%' }}
                    onChange={(option) => {
                      onChange(option)
                      clearErrors('useHouseholdTool')
                    }}
                    options={YES_OR_NO}
                  />
                )}
              />

              <Controller
                name="reportFormat"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    value={value}
                    label="Report Format"
                    style={{ width: '100%' }}
                    onChange={(option) => {
                      onChange(option)
                      clearErrors('reportFormat')
                    }}
                    options={REPORT_OPTIONS}
                  />
                )}
              />
            </div>

            <div className="flex flex-row gap-2">
              <TextInput fullWidth label="Color" name="color" {...register('color')} />

              <TextInput fullWidth label="Logo" name="logo" {...register('logo')} />
            </div>

            <Controller
              name="orientation"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  value={value}
                  label="Orientation"
                  style={{ width: '100%' }}
                  onChange={(option) => {
                    onChange(option)
                    clearErrors('orientation')
                  }}
                  options={ORIENTATION}
                />
              )}
            />

            <TextArea fullWidth label="Footnote" name="footnote" {...register('footnote')} />

            <TextInput
              fullWidth
              label="Report Benchmark Name"
              name="reportBenchmarkName"
              {...register('reportBenchmarkName')}
            />

            <TextArea
              fullWidth
              label="Descriptions"
              name="descriptions"
              {...register('descriptions')}
            />
          </form>
        }
        actions={[
          {
            type: 'submit',
            label: 'Add',
            onClick: handleSubmit(onSubmit),
          },
          {
            type: 'cancel',
            label: 'Cancel',
            onClick: () => {
              setShowAddDisclosureModal(false)
              reset()
            },
          },
        ]}
      />
    </PageContainer>
  )
})

export default Disclosures
